import React, { useRef, useCallback } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import { gsap } from 'gsap';

export type AnimationVariant = 'scale' | 'heart-pop' | 'share-burst' | 'tooltip-bounce';

export interface AnimatedInteractionProps {
  /** Content to animate */
  children: React.ReactNode;
  /** Preset animation variant */
  variant?: AnimationVariant;
  /** Scale value on hover (default: 1.05) */
  hoverScale?: number;
  /** Opacity on hover (default: 0.9) */
  hoverOpacity?: number;
  /** Scale value on click/active (default: 0.98) */
  activeScale?: number;
  /** Animation duration for hover in seconds (default: 0.2) */
  hoverDuration?: number;
  /** Animation duration for click in seconds (default: 0.1) */
  activeDuration?: number;
  /** GSAP easing function (default: 'power2.out') */
  ease?: string;
  /** Trigger animation on mount (for tooltip-bounce variant) */
  triggerOnMount?: boolean;
  /** Additional sx props */
  sx?: SxProps<Theme>;
  /** Additional Box props (className, id, data-*, aria-*, etc.) */
  className?: string;
  id?: string;
  'data-testid'?: string;
  'aria-label'?: string;
  role?: string;
}

/**
 * AnimatedInteraction - Atomic component for GSAP-powered hover/click animations
 * 
 * Wraps content with smooth scale and opacity animations on user interaction.
 * 
 * Layer 1: Atomic Component
 * - Pure presentation, no domain knowledge
 * - Accepts explicit animation config props
 * - Does NOT handle onClick or other event handlers (children should handle their own events)
 * 
 * @example
 * ```tsx
 * <AnimatedInteraction>
 *   <button onClick={handleClick}>Click me</button>
 * </AnimatedInteraction>
 * ```
 * 
 * @example Custom animation values
 * ```tsx
 * <AnimatedInteraction 
 *   hoverScale={1.1} 
 *   activeScale={0.95}
 *   hoverDuration={0.3}
 * >
 *   <div>Animated content</div>
 * </AnimatedInteraction>
 * ```
 */
export function AnimatedInteraction({
  children,
  hoverScale = 1.05,
  hoverOpacity = 0.9,
  activeScale = 0.98,
  hoverDuration = 0.2,
  activeDuration = 0.1,
  ease = 'power2.out',
  sx,
  ...props
}: AnimatedInteractionProps) {
  const ref = useRef<HTMLDivElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (ref.current) {
      gsap.to(ref.current, {
        scale: hoverScale,
        opacity: hoverOpacity,
        duration: hoverDuration,
        ease,
      });
    }
  }, [hoverScale, hoverOpacity, hoverDuration, ease]);

  const handleMouseLeave = useCallback(() => {
    if (ref.current) {
      gsap.to(ref.current, {
        scale: 1,
        opacity: 1,
        duration: hoverDuration,
        ease,
      });
    }
  }, [hoverDuration, ease]);

  const handleMouseDown = useCallback(() => {
    if (ref.current) {
      gsap.to(ref.current, {
        scale: activeScale,
        duration: activeDuration,
        ease,
      });
    }
  }, [activeScale, activeDuration, ease]);

  const handleMouseUp = useCallback(() => {
    if (ref.current) {
      gsap.to(ref.current, {
        scale: hoverScale,
        duration: activeDuration,
        ease,
      });
    }
  }, [hoverScale, activeDuration, ease]);

  return (
    <Box
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      sx={sx}
      {...props}
    >
      {children}
    </Box>
  );
}

export default AnimatedInteraction;
